import { useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { channelOperations } from '../../../../models/channels';
import {
  trackChannelFollow,
  trackChannelUnfollow,
} from '../../../../models/channels/analytics';
import { Button } from '../../../ui';
import { useFollowChannelButtonState } from '../../../../common/channel/use-follow-channel-button-state';
import { ChannelButtonTooltip } from '../../../channel/channel-button-tooltip';

type ChannelFollowButtonProps = {
  isFollowing: boolean;
  followingText: string;
  notFollowingText: string;
  handleClick: () => void;
};

const ChannelFollowButton = ({
  isFollowing,
  followingText,
  notFollowingText,
  handleClick,
}: ChannelFollowButtonProps) => {
  const icon = isFollowing ? 'done' : 'add';
  const theme = isFollowing ? 'secondary' : 'primary';
  const label = isFollowing ? followingText : notFollowingText;

  return (
    <Button
      icon={icon}
      theme={theme}
      size={'compact'}
      onClick={handleClick}
      aria-label={label}
      style={{ zIndex: 1 }} //safari fix, make sure this appears on top of shadow overflow padding on .content-layout__horizontal-scroll
    >
      {label}
    </Button>
  );
};

type ChannelFollowButtonContainerProps = {
  channelId: string;
  analyticsData: unknown;
};

const ChannelFollowButtonContainer = ({
  channelId,
  analyticsData,
}: ChannelFollowButtonContainerProps) => {
  const dispatch = useDispatch();
  const { label, disabled, following, aria } = useFollowChannelButtonState({
    channelId,
  });

  const handleClick = useCallback(() => {
    if (following) {
      dispatch(channelOperations.unfollowChannel(channelId));
      trackChannelUnfollow(channelId, analyticsData);
    } else {
      dispatch(channelOperations.followChannel(channelId));
      trackChannelFollow(channelId, analyticsData);
    }
  }, [dispatch, channelId, analyticsData, following]);

  return (
    <ChannelButtonTooltip channelId={channelId}>
      <Button
        icon={following ? 'done' : 'add'}
        theme={following ? 'secondary' : 'primary'}
        size={'compact'}
        disabled={disabled}
        onClick={handleClick}
        {...aria}
      >
        {label}
      </Button>
    </ChannelButtonTooltip>
  );
};

export default ChannelFollowButtonContainer;
